import { useCallback, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import ModalBasicLayout from "../../../Modal/ModalBasicLayout";
import CardMedia from "../../../Media/CardMedia";
import Button from "../../../Buttons/Button";
import Loader from "../../../Loaders/Loader";
import ModalGallery from "../../../Modal/ModalGallery";
import FormatMenuButton from "../../../Gallery/FormatMenuButton";
import { useTranslationApp } from "../../../../lib/i18next";
import { getExtensionFile } from "../../../../utils/string";
import {
  ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION,
  FORMATS_MEDIA_META,
  META,
  MIN_DIMENSIONS_CONTENT,
  VIDEO_FORMATS,
} from "../../../../utils/constants";
import {
  MODAL_EDIT_GROUP_CREATIVE_META_DESCRIPTION,
  PLACEMENT_META_RESIZE,
  PLACEMENT_META_SELECT_OTHER_CONTENT,
  PLACEMENT_META_USE,
} from "../../../../i18n/keysTranslations";
import { useResizeImageToLibrary } from "../../../../hooks/useResizeImageToLibrary";

const sxActionBarItem = {
  display: "inline-flex",
  alignItems: "center",
  lineHeight: 0,
  m: 0,
  p: 0,
  flexShrink: 0,
};

const sxActionButton = {
  m: 0,
  height: 40,
  minHeight: 40,
  py: 0,
  flexShrink: 0,
};

const ModalEditGroupCreativeMeta = ({
  modalOpen = false,
  onCloseModal = () => {},
  groupId,
  initialMediaUrl = "",
  language,
  destinationType,
  pathStorage,
  onConfirm = () => {},
}) => {
  const [draftMediaUrl, setDraftMediaUrl] = useState(initialMediaUrl);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { t } = useTranslationApp();
  const { resizeImageToLibrary, isResizing } = useResizeImageToLibrary();

  const extension = useMemo(
    () => getExtensionFile(draftMediaUrl),
    [draftMediaUrl]
  );
  const isVideo = VIDEO_FORMATS.includes(extension);
  const showResize = Boolean(draftMediaUrl) && !isVideo;
  const hasChanges = draftMediaUrl !== initialMediaUrl;

  const showGalleryOption =
    destinationType !== ADS_CAMPAIGN_META_DESTINATION_TYPE_ON_POST_OPTION;
  const showUploadDeviceOption = showGalleryOption;

  useEffect(() => {
    if (modalOpen) {
      setDraftMediaUrl(initialMediaUrl || "");
    }
  }, [modalOpen, initialMediaUrl]);

  const onResizeFormat = useCallback(
    async ({ format, formats, width, height }) => {
      const resolvedFormats = Array.isArray(formats)
        ? formats
        : format
          ? [format]
          : [];
      const targetFormat = resolvedFormats[0];
      if (!targetFormat) return;

      const result = await resizeImageToLibrary({
        sourceUrl: draftMediaUrl,
        format: targetFormat,
        width,
        height,
      });
      if (result.success && result.url) {
        setDraftMediaUrl(result.url);
      }
    },
    [draftMediaUrl, resizeImageToLibrary]
  );

  const onGallerySelected = useCallback((items) => {
    const mediaUrl = items?.[0]?.url || "";
    if (mediaUrl) {
      setDraftMediaUrl(mediaUrl);
    }
    setIsGalleryOpen(false);
  }, []);

  const onUse = useCallback(async () => {
    if (!draftMediaUrl) return;

    setIsSaving(true);
    try {
      await onConfirm({ groupId, mediaUrl: draftMediaUrl });
      onCloseModal();
    } finally {
      setIsSaving(false);
    }
  }, [draftMediaUrl, groupId, onConfirm, onCloseModal]);

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        fullWidth={true}
        fullScreen
        scroll="paper"
        zIndex={1400}
        sxContent={{
          p: 0,
          pl: 3,
          pb: 2,
          mt: 1,
        }}
        sxTitle={{
          py: 1,
        }}
      >
        <Box
          sx={{
            height: { xs: "70vh", sm: "80vh" },
            display: "flex",
            justifyContent: "center",
            px: 1,
            position: "relative",
          }}
          className="modal-media-content"
        >
          {isResizing && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2,
                bgcolor: "rgba(255,255,255,0.6)",
              }}
            >
              <Loader hasMarginTop={false} />
            </Box>
          )}

          {!draftMediaUrl ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                color: "text.secondary",
                height: "100%",
                width: "100%",
              }}
            >
              <ImageNotSupportedIcon sx={{ fontSize: 64 }} />
              <Typography variant="body1" align="center">
                {t(MODAL_EDIT_GROUP_CREATIVE_META_DESCRIPTION)}
              </Typography>
            </Box>
          ) : isVideo ? (
            <CardMedia
              url={draftMediaUrl}
              propsType={{
                controls: true,
                controlsList: "nodownload",
                autoplay: true,
                loop: true,
                muted: true,
              }}
              sxVideo={{
                width: "auto",
                height: "100%",
                maxWidth: "100%",
                objectFit: "contain",
                boxShadow: "none",
                borderRadius: 3,
              }}
            />
          ) : (
            <Avatar
              variant="rounded"
              src={draftMediaUrl}
              alt="Media"
              sx={{
                py: 0.25,
                height: "100%",
                width: "100%",
                ".MuiAvatar-img": {
                  width: "auto",
                  objectFit: "contain",
                  objectPosition: "top",
                },
              }}
            />
          )}
        </Box>

        <Box
          sx={{
            height: { xs: "22vh", sm: "13vh" },
            display: "flex",
            alignItems: { xs: "flex-end", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            width: "100%",
            px: 2,
            pb: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
              justifyContent: "center",
              width: "100%",
              "& > *": {
                m: 0,
              },
            }}
          >
            {showResize && (
              <Box sx={sxActionBarItem}>
                <FormatMenuButton
                  variant="resizeOnly"
                  url={draftMediaUrl}
                  onResizeFormat={onResizeFormat}
                  mode="button"
                  disabled={isResizing || isSaving}
                  loading={isResizing}
                  colorButton="secondary"
                  textButton={PLACEMENT_META_RESIZE}
                  sx={sxActionButton}
                />
              </Box>
            )}

            {showGalleryOption && (
              <Box sx={sxActionBarItem}>
                <Button
                  color="secondary"
                  size="small"
                  onClick={() => setIsGalleryOpen(true)}
                  disabled={isResizing || isSaving}
                  sx={sxActionButton}
                >
                  {t(PLACEMENT_META_SELECT_OTHER_CONTENT)}
                </Button>
              </Box>
            )}

            <Box sx={sxActionBarItem}>
              <Button
                color="primary"
                size="small"
                disabled={
                  !draftMediaUrl || !hasChanges || isResizing || isSaving
                }
                loading={isSaving}
                onClick={onUse}
                sx={sxActionButton}
              >
                {t(PLACEMENT_META_USE)}
              </Button>
            </Box>
          </Box>
        </Box>
      </ModalBasicLayout>

      {isGalleryOpen && showGalleryOption && (
        <ModalGallery
          modalOpen={isGalleryOpen}
          onCloseModal={() => setIsGalleryOpen(false)}
          multiple={false}
          onCallbackSelectedItems={onGallerySelected}
          pathStorage={pathStorage}
          modeUpload="sync"
          acceptedFormats={FORMATS_MEDIA_META}
          minDimensions={MIN_DIMENSIONS_CONTENT}
          showUploadDeviceOption={showUploadDeviceOption}
          showGalleryOption
          showAdAccountOption
          showSocialPostsOption
          initialValues={{ language, platform: META }}
          closeActionsDisabled={false}
          dialogZIndex={1500}
        />
      )}
    </>
  );
};

export default ModalEditGroupCreativeMeta;
